import React, { useState } from 'react'
import clsx from 'clsx'
import { Box, Typography } from '@mui/material'
import MemoImageViewer from 'src/components/molecules/MemoImageViewer'
import styles from './styles.module.scss'

export type ReportMessage = {
  id: string
  date: string
  reportType: reportTypeEnum
  reportIndex?: number
  messageBody?: string
  images?: string[]
}

export enum reportTypeEnum {
  Normal = 'normal',
  Resent = 'resent',
}

export type ReportMessagesProps = {
  reportMessages: ReportMessage[]
} & React.StyledProps

// 開いている画像の位置。メッセージ単位で images を持つため id とセットで保持する。
type OpenedImage = {
  messageId: string
  index: number
}

const reportTitle = (type: reportTypeEnum, reportIndex?: number) => {
  if (type === reportTypeEnum.Normal) {
    return '通常報告'
  }
  if (type === reportTypeEnum.Resent && typeof reportIndex === 'number') {
    return `再送信${reportIndex + 1}回目`
  }
  return ''
}

// 署名付きURL(https)のみを許可し、javascript: など不正スキームの混入による
// XSS（管理者がサムネイルを開いた際のスクリプト実行）を防ぐ。
// presenter 側の「画像があるか」判定と描画時のフィルタが食い違わないよう export する。
export const isSafeImageUrl = (src: string): boolean =>
  src.startsWith('https://')

const safeImagesOf = (images?: string[]): string[] =>
  images?.filter(isSafeImageUrl) ?? []

// 署名付きURLはクエリ(署名)が再取得のたびに変わるため、key には不変なパス部分を使う
const imageKeyOf = (src: string): string => src.split('?')[0]

const ReportMessages: React.StyledFC<ReportMessagesProps> = ({
  className,
  reportMessages,
}: ReportMessagesProps) => {
  const [openedImage, setOpenedImage] = useState<OpenedImage | null>(null)
  // 署名付きURLの期限切れ等で読み込みに失敗したサムネイル。壊れた画像アイコンの
  // まま放置せず、プレースホルダーに差し替えるために保持する。
  // 署名を含む src 全体をキーにするため、再取得で新しい署名付きURLが渡されれば
  // 別キーになり自動的に読み込みが再試行される（期限切れからの復旧）。
  const [failedThumbnails, setFailedThumbnails] = useState<Set<string>>(
    new Set()
  )

  const openedMessage = reportMessages.find(
    (message) => message.id === openedImage?.messageId
  )
  const openedImages = safeImagesOf(openedMessage?.images)

  return (
    <>
      {reportMessages.map((message) => {
        const images = safeImagesOf(message.images)
        return (
          <Box className={className} key={message.id}>
            <Box className={styles.reportMessage}>
              <Typography className={styles.fontInfo}>
                {reportTitle(message.reportType, message.reportIndex)}
              </Typography>
              <Typography className={clsx(styles.date, styles.fontInfo)}>
                {message.date}
              </Typography>
            </Box>
            {/* 画像のみのメモでは本文が無いので、空の本文行を描画しない */}
            {message.messageBody && (
              <Typography className={clsx(styles.messageBody, styles.fontInfo)}>
                {message.messageBody}
              </Typography>
            )}
            {images.length > 0 && (
              <Box className={styles.imageGrid}>
                {images.map((src, idx) => {
                  // 同一画像を複数枚添付しても key が衝突しないよう idx を含める
                  const thumbnailKey = `${message.id}-${idx}-${imageKeyOf(src)}`
                  // 失敗の記録は署名込みの src 単位。URLが更新されれば再試行される
                  const failureKey = `${message.id}-${idx}-${src}`
                  const isFailed = failedThumbnails.has(failureKey)
                  return (
                    <button
                      key={thumbnailKey}
                      type="button"
                      className={styles.imageThumbnailButton}
                      onClick={(): void =>
                        setOpenedImage({ messageId: message.id, index: idx })
                      }
                      disabled={isFailed}
                      aria-label={`補足事項添付画像${idx + 1}を開く`}
                    >
                      {isFailed ? (
                        <span className={styles.imageThumbnailFallback}>
                          画像を表示できません
                        </span>
                      ) : (
                        <img
                          className={styles.imageThumbnail}
                          src={src}
                          alt={`補足事項添付画像${idx + 1}`}
                          draggable={false}
                          onError={(): void =>
                            setFailedThumbnails((prev) =>
                              new Set(prev).add(failureKey)
                            )
                          }
                        />
                      )}
                    </button>
                  )
                })}
              </Box>
            )}
          </Box>
        )
      })}
      {openedImage && openedImages.length > 0 && (
        <MemoImageViewer
          images={openedImages}
          index={openedImage.index}
          subText={openedMessage?.date}
          onChangeIndex={(index): void =>
            setOpenedImage({ ...openedImage, index })
          }
          onClose={(): void => setOpenedImage(null)}
        />
      )}
    </>
  )
}
export default ReportMessages
